import { useCallback, useEffect, useState } from "react";
import type { Autonomy, ChatMode, ThinkingLevel } from "@/api/chat";

/** "12:20 AM" today, "Oct 5, 2026, 8:02 AM" otherwise. */
export function timeLabel(v: string | null | undefined): string {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function fullTimeLabel(v: string | null | undefined): string {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function prettyJson(v: unknown, max = 20_000): string {
  if (v === undefined) return "—";
  let s: string;
  try {
    s = typeof v === "string" ? tryReformat(v) : JSON.stringify(v, null, 2);
  } catch {
    s = String(v);
  }
  return s.length > max ? `${s.slice(0, max)}\n… (truncated)` : s;
}

function tryReformat(s: string): string {
  const t = s.trim();
  if ((t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"))) {
    try {
      return JSON.stringify(JSON.parse(t), null, 2);
    } catch {
      return s;
    }
  }
  return s;
}

export function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v : undefined;
}

export function obj(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
}

export function isVideoUrl(url: string | null | undefined): boolean {
  return !!url && /\.(mp4|mov|webm|m4v)(\?|#|$)/i.test(url);
}

// ─── localStorage-backed prefs (per browser) ───────────────────────────────

function readLs<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeLs(key: string, v: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* private mode */
  }
}

export interface ComposerPrefs {
  mode: ChatMode;
  thinkingLevel: ThinkingLevel;
  imageModel: string | null;
  videoModel: string | null;
  videoResolution: string | null;
  autonomy: Autonomy;
}

const PREFS_KEY = "mimic.chat.prefs.v2";
const DEFAULT_PREFS: ComposerPrefs = { mode: "thinking", thinkingLevel: "quick", imageModel: null, videoModel: null, videoResolution: null, autonomy: "auto_ask" };
const prefListeners = new Set<(p: ComposerPrefs) => void>();

export function useComposerPrefs(): [ComposerPrefs, (patch: Partial<ComposerPrefs>) => void] {
  const [prefs, setPrefs] = useState<ComposerPrefs>(() => ({ ...DEFAULT_PREFS, ...readLs<Partial<ComposerPrefs>>(PREFS_KEY, {}) }));
  useEffect(() => {
    const l = (p: ComposerPrefs) => setPrefs(p);
    prefListeners.add(l);
    return () => {
      prefListeners.delete(l);
    };
  }, []);
  const update = useCallback((patch: Partial<ComposerPrefs>) => {
    const next = { ...DEFAULT_PREFS, ...readLs<Partial<ComposerPrefs>>(PREFS_KEY, {}), ...patch };
    writeLs(PREFS_KEY, next);
    for (const l of prefListeners) l(next);
  }, []);
  return [prefs, update];
}

const FEEDBACK_KEY = "mimic.chat.feedback";

/** Thumbs up/down per message (kept in this browser — the server has no feedback endpoint for chat yet). */
export function useMessageFeedback(messageId: string): ["up" | "down" | null, (v: "up" | "down" | null) => void] {
  const [v, setV] = useState<"up" | "down" | null>(() => readLs<Record<string, "up" | "down">>(FEEDBACK_KEY, {})[messageId] ?? null);
  const set = useCallback(
    (next: "up" | "down" | null) => {
      const all = readLs<Record<string, "up" | "down">>(FEEDBACK_KEY, {});
      if (next) all[messageId] = next;
      else delete all[messageId];
      const keys = Object.keys(all);
      if (keys.length > 500) for (const k of keys.slice(0, keys.length - 500)) delete all[k];
      writeLs(FEEDBACK_KEY, all);
      setV(next);
    },
    [messageId],
  );
  return [v, set];
}

export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return;
    const m = window.matchMedia(query);
    const h = () => setMatch(m.matches);
    h();
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, [query]);
  return match;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export const AUTONOMY_LABEL: Record<Autonomy, string> = {
  confirm: "Confirm",
  auto_ask: "Auto",
  auto: "Autopilot",
};

export function asAutonomy(v: unknown): Autonomy | null {
  return v === "confirm" || v === "auto_ask" || v === "auto" ? v : null;
}

export function asMode(v: unknown): ChatMode | null {
  return v === "instant" || v === "thinking" ? v : v === "quick" ? "instant" : v === "deep" ? "thinking" : null;
}

export function asLevel(v: unknown): ThinkingLevel | null {
  return v === "quick" || v === "standard" || v === "thorough" || v === "comprehensive" ? v : null;
}

export const THINKING_LEVELS: { id: ThinkingLevel; label: string; description: string }[] = [
  { id: "quick", label: "Quick", description: "Quick planning for straightforward creation or lookup." },
  { id: "standard", label: "Standard", description: "Balanced planning for most marketing workflows." },
  { id: "thorough", label: "Thorough", description: "More careful analysis for evidence-based decisions." },
  { id: "comprehensive", label: "Comprehensive", description: "Deepest reasoning for complex, multi-step strategy." },
];

/** Tool name → the status verb shown while it runs ("Analyzing image…"). */
export function toolVerb(name: string | undefined): string {
  const n = (name ?? "").toLowerCase();
  if (!n) return "Working…";
  if (/vision|image_analy|analy[sz]e_image|qa\b|qa_|describe|inspect/.test(n)) return "Analyzing image…";
  if (/references\.analyze|video|transcri|frame/.test(n) && /analy|transcri|frame/.test(n)) return "Analyzing video…";
  if (/generate|regenerate|iterate|plan_matrix|matrix/.test(n)) return "Generating…";
  if (/report|brief|insight|monitor|metrics|performance|timeseries|overview/.test(n)) return "Analyzing performance…";
  if (/competitor|inspiration|references|ideas|sweep|discover|search|scrape|onboard|rescrape/.test(n)) return "Researching…";
  if (/publish|activate|launch|budget|pause/.test(n)) return "Preparing launch…";
  if (/schedule|cron/.test(n)) return "Scheduling…";
  return "Working…";
}
